import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type {
  Card,
  CursorPage,
  ReceivingAccount,
  Transaction,
  WalletSummary,
} from '@/types/financial-workspace';

const emptyCursorPage = <T,>(): CursorPage<T> => ({
  items: [],
  nextCursor: null,
  pageSize: 10,
});

export type WalletDetailState = {
  summary: WalletSummary | null;
  activeCurrency: string;
  transactions: CursorPage<Transaction>;
  receivingAccounts: CursorPage<ReceivingAccount>;
  cards: CursorPage<Card>;
  loaded: {
    activity: boolean;
    receivingAccounts: boolean;
    cards: boolean;
  };
};

export const walletDetailInitialState: WalletDetailState = {
  summary: null,
  activeCurrency: '',
  transactions: emptyCursorPage<Transaction>(),
  receivingAccounts: emptyCursorPage<ReceivingAccount>(),
  cards: emptyCursorPage<Card>(),
  loaded: {
    activity: false,
    receivingAccounts: false,
    cards: false,
  },
};

const slice = createSlice({
  name: 'wallet-detail',
  initialState: walletDetailInitialState,
  reducers: {
    resetWalletDetailAction: () => walletDetailInitialState,
    setWalletSummaryAction(state, { payload }: PayloadAction<WalletSummary>) {
      state.summary = payload;
      state.activeCurrency = payload.currencyBalances[0]?.currency ?? payload.primaryCurrency.split('/')[0];
    },
    setWalletDetailCurrencyAction(state, { payload }: PayloadAction<string>) {
      state.activeCurrency = payload;
    },
    setWalletTransactionsAction(state, { payload }: PayloadAction<CursorPage<Transaction>>) {
      state.transactions = payload;
      state.loaded.activity = true;
    },
    appendWalletTransactionsAction(state, { payload }: PayloadAction<CursorPage<Transaction>>) {
      const existingIds = new Set(state.transactions.items.map((transaction) => transaction.id));
      state.transactions.items = [
        ...state.transactions.items,
        ...payload.items.filter((transaction) => !existingIds.has(transaction.id)),
      ];
      state.transactions.nextCursor = payload.nextCursor;
      state.transactions.hasMore = payload.hasMore;
      state.transactions.asOf = payload.asOf ?? state.transactions.asOf;
      state.loaded.activity = true;
    },
    setWalletReceivingAccountsAction(state, { payload }: PayloadAction<CursorPage<ReceivingAccount>>) {
      state.receivingAccounts = payload;
      state.loaded.receivingAccounts = true;
    },
    setWalletCardsAction(state, { payload }: PayloadAction<CursorPage<Card>>) {
      state.cards = payload;
      state.loaded.cards = true;
    },
    updateWalletDetailCardAction(state, { payload }: PayloadAction<Card>) {
      state.cards.items = state.cards.items.map((card) =>
        card.id === payload.id ? payload : card,
      );
    },
  },
});

export const {
  resetWalletDetailAction,
  setWalletSummaryAction,
  setWalletDetailCurrencyAction,
  setWalletTransactionsAction,
  appendWalletTransactionsAction,
  setWalletReceivingAccountsAction,
  setWalletCardsAction,
  updateWalletDetailCardAction,
} = slice.actions;

export default slice.reducer;
